import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtGBP } from "@/lib/format";
import { toast } from "sonner";
import { Telescope, Check, Sparkles, ArrowLeft } from "lucide-react";

export default function Pricing() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("year");
  const { data: plans = [] } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });

  const choose = async (planId) => {
    if (!user) { navigate("/login"); return; }
    try {
      const res = await api.post("/subscribe", { plan: planId });
      setUser(res.data);
      toast.success(`You're now on the ${planId} plan`);
    } catch { toast.error("Could not change plan"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0D14] text-slate-200">
      <header className="h-16 flex items-center justify-between px-4 lg:px-8 border-b border-slate-800/80">
        <Link to="/" className="flex items-center gap-2.5"><Telescope className="w-6 h-6 text-amber-400" /><span className="font-serif text-xl font-semibold">AuctionScope</span></Link>
        <Link to={user ? "/dashboard" : "/login"} className="al-btn-ghost text-sm"><ArrowLeft className="w-4 h-4" /> {user ? "Back to app" : "Sign in"}</Link>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/90">Subscriptions</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-2">Choose the right plan</h1>
          <p className="mt-4 text-sm text-slate-400">The world's auction results, normalized into one ledger — at a fraction of the cost. Compare: Artprice charges $335–$685/yr. We start free and undercut every paid tier.</p>
          <div className="inline-flex items-center gap-1 p-1 al-card mt-6">
            <button data-testid="cycle-year" onClick={() => setCycle("year")} className={`px-4 py-1.5 rounded-md text-sm ${cycle === "year" ? "bg-amber-500 text-[#0A0D14] font-medium" : "text-slate-400"}`}>Annual <span className="text-[10px]">(save more)</span></button>
            <button data-testid="cycle-month" onClick={() => setCycle("month")} className={`px-4 py-1.5 rounded-md text-sm ${cycle === "month" ? "bg-amber-500 text-[#0A0D14] font-medium" : "text-slate-400"}`}>Monthly</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-10">
          {plans.map((p) => {
            const price = cycle === "year" ? p.price_year : p.price_month;
            const current = user?.plan === p.id;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`al-card p-6 flex flex-col relative ${p.popular ? "border-amber-500/50 ring-1 ring-amber-500/20" : ""}`}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 al-chip bg-amber-500 text-[#0A0D14] font-semibold"><Sparkles className="w-3 h-3" /> Most popular</span>}
                <h3 className="font-serif text-2xl">{p.name}</h3>
                <p className="text-xs text-slate-400 mt-1 h-8">{p.tagline}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="mono-num text-4xl font-semibold text-slate-100">{price === 0 ? "£0" : fmtGBP(price)}</span>
                  <span className="text-xs text-slate-500 mb-1.5">{price === 0 ? "free forever" : `/${cycle === "year" ? "yr" : "mo"}`}</span>
                </div>
                {p.savings_pct ? (
                  <p className="mt-1 text-[11px] text-emerald-400 font-mono">Save {p.savings_pct}% vs {p.competitor_name} (£{p.competitor_year}/yr)</p>
                ) : <p className="mt-1 text-[11px] text-slate-600 font-mono">&nbsp;</p>}
                <button data-testid={`choose-${p.id}`} onClick={() => choose(p.id)} disabled={current}
                  className={`mt-5 w-full py-2.5 rounded-md text-sm font-medium transition-colors ${current ? "border border-slate-700 text-slate-500" : p.popular ? "al-btn" : "al-btn-ghost"}`}>
                  {current ? "Current plan" : p.id === "free" ? (user ? "Switch to Free" : "Sign up free") : "Choose " + p.name}
                </button>
                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-slate-600 mt-10">Demo billing — selecting a plan activates it instantly without payment. Real card payments can be wired on request.</p>
      </div>
    </div>
  );
}
